import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, Leaf, Calendar, Phone, Star, CheckCircle, Baby, Users } from 'lucide-react';
import { BOOKING_URL } from "@/constants/urls";
import BlogPostingSchema from '@/components/BlogPostingSchema';
import fertilityImageWebP from "@/assets/family.webp";
import fertilityImageJPG from "@/assets/family.jpg";
import preconceptionImageWebP from "@/assets/blog/3month-preconception.webp";
import preconceptionImagePNG from "@/assets/blog/3month-preconception.png";

const PregnancyPreparationBlog: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update document title and meta tags
    document.title = "Preparing for Pregnancy: Chinese Medicine + Women's Health Nutrition | Women and the Way";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Discover how combining Chinese medicine with dietitian-led women's health nutrition can support your fertility journey. Preconception care with Dr. Kirby Quin and Rachel Hazlett on the Sunshine Coast.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Preparing for Pregnancy: How Chinese Medicine + Women's Health Nutrition Can Support Your Fertility");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Learn how our two-practitioner approach—Chinese medicine with Rachel Hazlett, women's health dietitian—can help you prepare for pregnancy with clear, practical nutrition support.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/pregnancy-preparation-chinese-medicine-nutrition");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/assets/family.webp");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Preparing for Pregnancy: Chinese Medicine + Women's Health Nutrition Support");
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', "Discover how our comprehensive fertility special can help you prepare for pregnancy with ancient wisdom and modern science.");
    }

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', "https://womenandtheway.com.au/assets/family.webp");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  return (
    <>
      <BlogPostingSchema
        title="Preparing for Pregnancy: How Chinese Medicine + Women's Health Nutrition Can Support Your Fertility"
        description="Discover how combining Chinese medicine with dietitian-led women's health nutrition supports your fertility journey. Preconception care with Dr. Kirby Quin and Rachel Hazlett, and our fertility special on the Sunshine Coast."
        image={fertilityImageWebP}
        url="https://womenandtheway.com.au/blog/pregnancy-preparation-chinese-medicine-nutrition"
        publishedDate="2025-08-08"
        modifiedDate="2026-05-04"
        keywords="fertility support, Chinese medicine, women's health dietitian, preconception care, pregnancy preparation, Sunshine Coast, acupuncture, natural fertility, hormonal balance, PCOS, endometriosis, fertility special"
        articleSection="Fertility & Preconception"
      />

      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="py-16 px-4 md:px-6 bg-gradient-to-br from-sage-50 to-amber-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-8">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 mb-4">
                Fertility & Preconception
              </Badge>
              <h1 className="font-serif text-4xl md:text-5xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent leading-tight mb-6">
                Preparing for Pregnancy: How Chinese Medicine + Women&apos;s Health Nutrition Can Support Your Fertility
              </h1>
              <p className="text-xl text-sage-600 leading-relaxed max-w-3xl mx-auto">
                Discover how combining ancient wisdom with modern science can help you feel confident, prepared, and empowered on your fertility journey.
              </p>
            </div>
            
            <div className="flex justify-center mb-8">
              <picture>
                <source srcSet={fertilityImageWebP} type="image/webp" />
                <img
                  src={fertilityImageJPG}
                  alt="Happy family planning and fertility support through Chinese medicine and women's health dietitian care"
                  className="rounded-2xl shadow-2xl max-w-2xl w-full h-auto"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                For many women, deciding to start or grow a family is one of life's most exciting moments. But it can also bring questions, uncertainty, and sometimes a little anxiety.
              </p>
              
              <div className="bg-sage-50 rounded-lg p-6 my-8">
                <ul className="text-sage-700 space-y-2 list-none">
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-500 font-semibold">"</span>
                    <span className="italic">How do I best prepare my body for pregnancy?</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-500 font-semibold">"</span>
                    <span className="italic">Are there natural ways to improve my chances?</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-amber-500 font-semibold">"</span>
                    <span className="italic">Where do I even start?</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                At <a href="/" className="text-amber-600 hover:text-amber-700 font-medium">Women and the Way</a> on the Sunshine Coast, we believe your fertility journey should be supported, informed, and empowering—not overwhelming. That's why we've created a <a href="/care-packages/fertility-package" className="text-amber-600 hover:text-amber-700 font-medium">Fertility Special</a> that blends Chinese medicine with dietitian-led women&apos;s health nutrition.
              </p>

              <p className="text-lg text-sage-700 leading-relaxed">
                This powerful combination can help you prepare your body, balance your hormones, and feel truly ready for the road ahead.
              </p>
            </div>
          </div>
        </section>

        {/* Why Preconception Care Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center justify-center mb-8">
              <picture>
                <source srcSet={preconceptionImageWebP} type="image/webp" />
                <img
                  src={preconceptionImagePNG}
                  alt="3-month preconception care timeline showing optimal preparation period for pregnancy with Chinese medicine and nutrition support"
                  className="rounded-lg shadow-lg max-w-md w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 flex items-center">
              <Leaf className="h-8 w-8 text-amber-500 mr-3" />
              🌿 Why Preconception Care Matters
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Pregnancy doesn't start the day you conceive—it starts in the months (or even years) leading up to it. In both <a href="/fertility-support" className="text-amber-600 hover:text-amber-700 font-medium">Chinese medicine</a> and functional medicine, the <a href="/preconception" className="text-amber-600 hover:text-amber-700 font-medium">preconception phase</a> is considered vital for laying strong foundations.
              </p>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Think of it like preparing soil before planting seeds. When the soil is rich, balanced, and nourished, the seeds have the best chance to thrive. Your body works the same way. By optimising your health now, you can:
              </p>

              <Card className="bg-white border-amber-200 shadow-md mb-8">
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Improve hormonal balance</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Support regular ovulation and menstrual cycles</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Reduce inflammation</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Boost nutrient stores (for you and your future baby)</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Enhance energy and emotional wellbeing</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sage-700">Minimise complications during pregnancy</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <p className="text-lg text-sage-700 leading-relaxed">
                The best part? These changes don't just help with fertility—they also support your long-term health.
              </p>
            </div>
          </div>
        </section>

        {/* Chinese Medicine for Fertility */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 flex items-center">
              <Baby className="h-8 w-8 text-amber-500 mr-3" />
              🌸 The Wisdom of Chinese Medicine for Fertility
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Chinese medicine has been supporting women's reproductive health for thousands of years. In this tradition, fertility is seen as the result of balance—between Yin and Yang, warmth and coolness, rest and activity.
              </p>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                <a href="/team#kirby" className="text-amber-600 hover:text-amber-700 font-medium">Dr. Kirby Quin</a>, our experienced Chinese medicine practitioner, uses gentle <a href="/fertility-support" className="text-amber-600 hover:text-amber-700 font-medium">acupuncture</a> and herbal medicine to help restore harmony in the body and address patterns that may affect fertility.
              </p>

              <Card className="bg-sage-50 border-sage-200 shadow-md mb-8">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-800 text-xl mb-4">Common patterns she supports include:</h3>
                  <ul className="space-y-3">
                    <li className="text-sage-700">
                      <strong>Kidney Jing Deficiency</strong> – related to reproductive energy and egg quality
                    </li>
                    <li className="text-sage-700">
                      <strong>Liver Qi Stagnation</strong> – often linked to stress, irregular cycles, and PMS
                    </li>
                    <li className="text-sage-700">
                      <strong>Blood Deficiency</strong> – affecting uterine lining health and implantation
                    </li>
                    <li className="text-sage-700">
                      <strong>Cold in the Uterus</strong> – reducing warmth needed for conception
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Research shows acupuncture can help regulate menstrual cycles, improve blood flow to the uterus, and reduce <a href="/stress-burnout" className="text-amber-600 hover:text-amber-700 font-medium">stress</a>—creating a more receptive environment for conception.
              </p>

              <blockquote className="bg-amber-50 border-l-4 border-amber-400 p-6 italic text-sage-700 text-lg leading-relaxed mb-6">
                "My role is to listen, observe, and create a plan that supports your whole being—not just your reproductive organs. Fertility is about balance, health, and feeling ready in every sense." – Dr. Kirby Quin
              </blockquote>
            </div>
          </div>
        </section>

        {/* Women's health nutrition (dietitian) */}
        <section className="py-16 px-4 md:px-6 bg-amber-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 flex items-center">
              <Heart className="h-8 w-8 text-amber-500 mr-3" />
              🥗 How a Women&apos;s Health Dietitian Supports Fertility
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                While Chinese medicine works to balance the body&apos;s internal energy, clinical nutrition looks at how food, digestion, biochemistry, and pathology results come together to support fertility and hormone health.
              </p>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Rachel Hazlett, our women&apos;s health dietitian, brings over ten years of experience supporting women through fertility, pregnancy, and hormone-related conditions—including PCOS, endometriosis, and digestive concerns—as well as those optimising nutrition when trying to conceive or during pregnancy.
              </p>

              <Card className="bg-white border-amber-200 shadow-md mb-8">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-800 text-xl mb-4">In your consult, Rachel typically reviews:</h3>
                  <ul className="space-y-3">
                    <li className="text-sage-700">
                      <strong>Your story and goals</strong> – medical history, symptoms, and what you want to feel different day to day
                    </li>
                    <li className="text-sage-700">
                      <strong>Hormonal and reproductive context</strong> – including patterns that may overlap with PCOS, endometriosis, or thyroid-related concerns where relevant
                    </li>
                    <li className="text-sage-700">
                      <strong>Blood tests and supplements</strong> – aligning diet with iron, vitamin D, folate, zinc, and other priorities your results suggest
                    </li>
                    <li className="text-sage-700">
                      <strong>Gut health and digestion</strong> – comfort, absorption, and inflammation as they relate to energy and hormones
                    </li>
                    <li className="text-sage-700">
                      <strong>Realistic eating patterns</strong> – clear, sustainable steps (including blood sugar rhythm and metabolic wellbeing) without fad diets
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Rachel&apos;s approach is thorough and practical: she connects history, tests, supplements, gut health, and the way you actually eat, so you leave with a plan you can stick to into <a href="/pregnancy" className="text-amber-600 hover:text-amber-700 font-medium">pregnancy</a> and beyond. She offers appointments in clinic and via telehealth.
              </p>

              <blockquote className="bg-sage-50 border-l-4 border-sage-400 p-6 italic text-sage-700 text-lg leading-relaxed mb-6">
                Rachel&apos;s focus is helping you feel confident about what to eat—making steady, realistic changes that support your health now and in the future.
              </blockquote>
            </div>
          </div>
        </section>

        {/* Why Combining Approaches Works */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 flex items-center">
              <Star className="h-8 w-8 text-amber-500 mr-3" />
              💡 Why Combining These Approaches Works So Well
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Both Chinese medicine and dietitian-led nutrition recognise that fertility is not just about the reproductive system—it's about whole-body health.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="bg-sage-50 border-sage-200 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <Leaf className="h-6 w-6 text-sage-600 mr-2" />
                      <h3 className="font-semibold text-sage-800">Chinese Medicine</h3>
                    </div>
                    <p className="text-sage-700">Supports the energetic and hormonal balance of the body.</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <Heart className="h-6 w-6 text-amber-600 mr-2" />
                      <h3 className="font-semibold text-sage-800">Women&apos;s Health Nutrition</h3>
                    </div>
                    <p className="text-sage-700">Maps your diet, digestion, and biochemistry to clear, practical steps you can sustain.</p>
                  </CardContent>
                </Card>
              </div>

              <p className="text-lg text-sage-700 leading-relaxed">
                Together, these approaches give you a comprehensive, personalised plan that addresses both the physical and emotional aspects of fertility.
              </p>
            </div>
          </div>
        </section>

        {/* Fertility Special */}
        <section className="py-16 px-4 md:px-6 bg-gradient-to-br from-sage-50 to-amber-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 text-center">
              📦 Introducing Our Fertility Special
            </h2>
            
            <p className="text-lg text-sage-700 leading-relaxed text-center mb-8">
              We've created a special package so you can experience the best of both worlds—at a price that makes it easier to get started.
            </p>

            <Card className="border-2 border-amber-200 shadow-xl bg-white max-w-3xl mx-auto">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 mb-4">
                    🌿 Fertility Special – Limited Time 🌿
                  </Badge>
                  <h3 className="font-serif text-2xl font-bold text-sage-800 mb-4">What's Included:</h3>
                </div>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sage-600 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sage-800 mb-2">Initial Fertility Acupuncture Consult with Dr. Kirby Quin</h4>
                      <p className="text-sage-600">Gentle acupuncture, Chinese medicine diagnosis, and a personalised treatment plan.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-amber-600 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sage-800 mb-2">Initial Fertility Nutrition Consult with Rachel Hazlett</h4>
                      <p className="text-sage-600">In-depth assessment, practical dietary guidance, and supplement considerations aligned with your history and test results—in clinic or by telehealth.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-sage-600 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sage-800 mb-2">Bonus eBooks from Both Practitioners</h4>
                      <p className="text-sage-600">Packed with cycle tracking tips, meal ideas, herbal insights, and more.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-sage-50 rounded-lg p-6 text-center mb-6">
                  <p className="text-lg text-sage-700 mb-2">
                    <span className="line-through text-sage-500">Valued at: $370</span>
                  </p>
                  <p className="text-2xl font-bold text-amber-600 mb-2">Special Price: $250</p>
                  <p className="text-sage-600">That's two separate one-on-one appointments plus resources you can keep and refer to throughout your journey.</p>
                </div>

                <div className="text-center">
                  <Button
                    asChild
                    size="lg"
                    className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                  >
                    <a
                      href="/care-packages/fertility-package"
                      aria-label="Learn more about our fertility special package"
                    >
                      Learn More About Our Fertility Special
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-8 text-center">
              🧭 What to Expect at Your Appointments
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-sage-50 border-sage-200 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Leaf className="h-6 w-6 text-sage-600 mr-2" />
                    <h3 className="font-semibold text-sage-800">With Kirby – Acupuncture & Chinese Medicine</h3>
                  </div>
                  <ul className="text-sage-700 space-y-2">
                    <li>• Detailed discussion of your health history and fertility journey</li>
                    <li>• Pulse and tongue diagnosis to assess internal balance</li>
                    <li>• Gentle acupuncture to support hormonal and emotional wellbeing</li>
                    <li>• Herbal recommendations (if appropriate)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-amber-50 border-amber-200 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Heart className="h-6 w-6 text-amber-600 mr-2" />
                    <h3 className="font-semibold text-sage-800">With Rachel – Women&apos;s Health Dietitian</h3>
                  </div>
                  <ul className="text-sage-700 space-y-2">
                    <li>• Review of medical history, relevant blood tests, supplements, gut health, and current eating patterns</li>
                    <li>• Clear, personalised nutrition priorities you can implement week to week</li>
                    <li>• Supplement discussion aligned with your needs and care team</li>
                    <li>• Meal and snack ideas for hormones, energy, and fertility goals</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <p className="text-lg text-sage-700 leading-relaxed text-center mt-8">
              Both practitioners will ensure their advice complements the other—so you walk away with a unified, practical plan.
            </p>
          </div>
        </section>

        {/* Who This Package Is For */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 flex items-center justify-center">
              <Users className="h-8 w-8 text-amber-500 mr-3" />
              🌺 Who This Package Is For
            </h2>
            
            <Card className="bg-white border-amber-200 shadow-md">
              <CardContent className="p-8">
                <p className="text-lg text-sage-700 leading-relaxed mb-6 text-center">
                  This special is ideal if you:
                </p>
                
                <ul className="space-y-4 max-w-2xl mx-auto">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sage-700">Are planning to conceive in the next 3–12 months</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sage-700">Have been trying for a while and want extra support</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sage-700">Experience irregular cycles, PMS, or hormonal symptoms</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sage-700">Have a history of <a href="/miscarriage-support" className="text-amber-600 hover:text-amber-700 font-medium">miscarriage</a> and want to strengthen your foundations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-sage-700">Want a natural, whole-body approach alongside (or before) medical fertility treatments</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Emotional Side */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 text-center">
              🪷 The Emotional Side of Fertility
            </h2>
            
            <div className="prose prose-lg max-w-none text-center">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Fertility isn't just physical—it's deeply emotional. Both Kirby and Rachel create a space where you can share your worries, hopes, and questions without judgment.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                You'll leave feeling heard, supported, and encouraged—not just given a list of "to-dos."
              </p>
            </div>
          </div>
        </section>

        {/* How to Book */}
        <section className="py-16 px-4 md:px-6 bg-gradient-to-br from-amber-50 to-sage-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 text-center">
              📍 How to Book Your Fertility Special
            </h2>
            
            <div className="text-center mb-8">
              <p className="text-lg text-sage-700 leading-relaxed mb-4">
                We're offering this special for a limited time and spaces are filling quickly.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Your journey starts with two separate appointments—one with Kirby and one with Rachel. Kirby sees you at our <a href="/currimundi-clinic" className="text-amber-600 hover:text-amber-700 font-medium">Sunshine Coast clinic</a>; Rachel offers in-clinic and telehealth nutrition consultations.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
              >
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Book your fertility special appointment online"
                >
                  ➡ Book Your Fertility Special
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
              >
                <a href="tel:0415537784" className="flex items-center" aria-label="Call us at 0415 537 784">
                  <Phone className="h-5 w-5 mr-2" />
                  Call 0415 537 784
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Final Word */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-sage-800 mb-6 text-center">
              🌞 A Final Word
            </h2>
            
            <div className="prose prose-lg max-w-none text-center">
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Your fertility journey is unique. There's no one-size-fits-all approach—and that's exactly why this combination works. By weaving together Chinese medicine with Rachel&apos;s dietitian expertise in women&apos;s health nutrition, you get a plan that&apos;s truly yours.
              </p>
              
              <p className="text-lg text-sage-700 leading-relaxed mb-6">
                Whether you're just starting to think about pregnancy or have been on this road for a while, our goal is simple: to help you feel calm, confident, and ready for the next chapter.
              </p>

              <div className="bg-gradient-to-r from-sage-50 to-amber-50 rounded-lg p-8 mt-8">
                <h3 className="font-serif text-2xl font-bold text-sage-800 mb-4">
                  <a href="/" className="text-sage-800 hover:text-amber-600">Women and the Way</a> – Sunshine Coast Fertility & Women's Health
                </h3>
                <p className="text-sage-700 italic">
                  Supporting women through every stage of life with compassionate, evidence-based care.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sage-800 mb-8 text-center">
              Related Articles
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white border-amber-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-800 mb-2">
                    <a href="/blog/preconception-window" className="hover:text-amber-600">The 3-Month Preconception Window</a>
                  </h3>
                  <p className="text-sage-600 text-sm">Learn why the 3 months before conception are crucial for both partners and how to optimise this window.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-amber-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-800 mb-2">
                    <a href="/blog/fertility-foods-hormone-health" className="hover:text-amber-600">Fertility Foods for Hormone Health</a>
                  </h3>
                  <p className="text-sage-600 text-sm">Discover the best foods to support hormonal balance and fertility naturally.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PregnancyPreparationBlog; 